import PropTypes from 'prop-types';
import React from 'react';
import Head from 'next/head';
import Image from 'next/image';

import Markdown from '@components/Newsroom/Markdown/Markdown';

import { getFullPressRelease } from '@services/newsroomService';
import { formatDate, formatCategories } from '@utils/formatters';
import PressReleaseArticleStyles from './PressReleaseArticle.styled';
import EmbeddedQuiz from '@components/Newsroom/EmbeddedContent/EmbeddedQuiz';
import EmbeddedContent from '@components/Newsroom/EmbeddedContent/EmbeddedContent';
import { formatHttpsUrl } from '@utils/utils';

const PressReleaseArticle = ({ ssrFullArticle }) => {
  return (
    ssrFullArticle && (
      <PressReleaseArticleStyles>
        <div className="constrained-width-newsroom">
          <Head>
            <title>{ssrFullArticle.titleTag}</title>
            <meta name="description" content={ssrFullArticle.metaDescription} />
            <meta
              property="og:image"
              content={ssrFullArticle.featuredMedia.media.url}
            />
          </Head>
          <div className="articleContainer">
            <p className="pr-categories">
              {ssrFullArticle.categories
                ? formatCategories(ssrFullArticle.categories)
                : ''}
            </p>
            <h1 className="h3">{ssrFullArticle.headline}</h1>
            <p className="subheadline">{ssrFullArticle.subHeadline}</p>
            <h6 className="publication-date">
              {formatDate(ssrFullArticle.publicationDate)}
            </h6>
          </div>
          <div className="pr-media">
            {ssrFullArticle.featuredMedia ? (
              <Image
                src={`${formatHttpsUrl(
                  ssrFullArticle.featuredMedia.media.url
                )}?w=1200&fm=webp`}
                alt={ssrFullArticle.featuredMedia.media.altText}
                width={808}
                height={540}
              />
            ) : null}
          </div>
          <div className="articleContainer">
            <Markdown
              markdown={ssrFullArticle && ssrFullArticle.articleContent1}
            />

            {/** reusable content modules go below content 1, unless there is no content 1 **/}
            {ssrFullArticle.reuseContentModules
              ? ssrFullArticle.reuseContentModules.map((module, i) =>
                  module.mediaType === 'Video' ? (
                    <EmbeddedContent key={`video${i}`} video={module} />
                  ) : (
                    <EmbeddedQuiz
                      key={`quiz${i}`}
                      mediaType={module.mediaType}
                      source={module.embeddedContent}
                      header={module.embeddedContentHeader}
                      intro={module.embeddedContentLongIntro}
                      disclaimer={module.disclaimer}
                    />
                  )
                )
              : null}

            <Markdown
              markdown={ssrFullArticle && ssrFullArticle.articleContent2}
            />

            {ssrFullArticle.boilerPlate &&
              ssrFullArticle.boilerPlate.map((text, index) => (
                <Markdown markdown={text.bodyText} key={index} />
              ))}
          </div>
        </div>
      </PressReleaseArticleStyles>
    )
  );
};

PressReleaseArticle.propTypes = {
  ssrFullArticle: PropTypes.shape({
    articleContent1: PropTypes.string,
    articleContent2: PropTypes.string,
    boilerPlate: PropTypes.arrayOf(
      PropTypes.shape({
        bodyText: PropTypes.string,
      })
    ),
    categories: PropTypes.array,
    featuredMedia: PropTypes.shape({
      media: PropTypes.shape({
        altText: PropTypes.string,
        url: PropTypes.string,
      }),
    }),
    headline: PropTypes.string,
    metaDescription: PropTypes.string,
    publicationDate: PropTypes.string,
    reuseContentModules: PropTypes.arrayOf(
      PropTypes.shape({
        embeddedContentHeader: PropTypes.any,
        embeddedContentLongIntro: PropTypes.string,
        embeddedContent: PropTypes.string,
        mediaType: PropTypes.string,
        videoSource: PropTypes.string,
        disclaimer: PropTypes.string,
      })
    ),
    subHeadline: PropTypes.string,
    titleTag: PropTypes.string,
  }),
};

export async function getServerSideProps({ params }) {
  let fullArticle = null;
  await getFullPressRelease(params?.slug)
    .then((result) => {
      fullArticle = result;
    })
    .catch((error) => {
      console.error(
        `Error retrieving Newsroom Press Release Article ${params?.slug}: ${error}`
      );
    });

  if (!fullArticle) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ssrFullArticle: fullArticle,
    },
  };
}

export default PressReleaseArticle;
